"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Zap, TrendingUp, TrendingDown, Flame, Snowflake } from "lucide-react";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";

interface XTrackerVelocityGaugeProps {
  handle?: string;
  title?: string;
  startDate: string;
  endDate: string;
}

interface DailyMetric {
  date: string;
  count: number;
  cumulative: number;
}

export default function XTrackerVelocityGauge({
  handle = "elonmusk",
  title,
  startDate,
  endDate,
}: XTrackerVelocityGaugeProps) {
  const [metrics, setMetrics] = useState<DailyMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [prevTotal, setPrevTotal] = useState<number | null>(null);
  const [isPulsing, setIsPulsing] = useState(false);
  const [newTweetsCount, setNewTweetsCount] = useState(0);
  const pulseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const visRef = useVisibilityRef();

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ handle, startDate, endDate });
      if (title) params.set("title", title);
      const res = await fetch(`/api/xtracker?${params.toString()}`);
      if (res.ok) {
        const result = await res.json();
        const newMetrics = result.metrics?.daily || [];
        setMetrics(newMetrics);

        // Check for new tweets (pulse effect)
        const currentTotal = newMetrics[newMetrics.length - 1]?.cumulative || 0;
        if (prevTotal !== null && currentTotal > prevTotal) {
          const diff = currentTotal - prevTotal;
          setNewTweetsCount(diff);
          setIsPulsing(true);

          // Clear previous timeout
          if (pulseTimeoutRef.current) {
            clearTimeout(pulseTimeoutRef.current);
          }

          // Stop pulsing after 3 seconds
          pulseTimeoutRef.current = setTimeout(() => {
            setIsPulsing(false);
            setNewTweetsCount(0);
          }, 3000);
        }
        setPrevTotal(currentTotal);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [handle, title, startDate, endDate, prevTotal]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => { if (visRef.current) fetchData(); }, 30000); // Check every 30s for more responsive pulse
    return () => {
      clearInterval(interval);
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
    };
  }, [fetchData]);

  if (loading || metrics.length < 2) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 h-40 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 dark:bg-white/10 rounded mb-4" />
        <div className="h-20 bg-gray-100 dark:bg-white/5 rounded-[5px]" />
      </div>
    );
  }

  // Calculate velocities
  const totalTweets = metrics[metrics.length - 1]?.cumulative || 0;
  const totalDays = metrics.length;
  const totalHours = totalDays * 24;

  // Average tweets per hour for the whole period
  const avgTweetsPerHour = totalHours > 0 ? totalTweets / totalHours : 0;

  // Recent velocity (last day vs previous)
  const lastDayCount = metrics[metrics.length - 1]?.count || 0;
  const prevDayCount = metrics.length > 1 ? metrics[metrics.length - 2]?.count || 0 : lastDayCount;
  const recentTweetsPerHour = lastDayCount / 24;

  // Velocity ratio (how much faster/slower than average)
  const velocityRatio = avgTweetsPerHour > 0 ? recentTweetsPerHour / avgTweetsPerHour : 1;

  // Determine status
  const isOnFire = velocityRatio > 1.3;
  const isCold = velocityRatio < 0.7;
  const trend = lastDayCount > prevDayCount ? "up" : lastDayCount < prevDayCount ? "down" : "stable";

  // Gauge angle calculation (-90 to 90 degrees, 0 is center/average)
  // velocityRatio of 0.5 = -90deg, 1.0 = 0deg, 1.5+ = 90deg
  const gaugeAngle = Math.max(-90, Math.min(90, (velocityRatio - 1) * 180));

  // Color based on velocity
  const getGaugeColor = () => {
    if (isOnFire) return "#FF6B35"; // Orange/fire
    if (isCold) return "#00D4FF"; // Cold blue
    return "#9CA3AF"; // Normal gray
  };

  const gaugeColor = getGaugeColor();

  return (
    <div
      className={`relative bg-white dark:bg-[#18181D] rounded-[5px] border overflow-hidden transition-colors ${
        isPulsing
          ? "border-[#7C3AED] dark:border-[#CCFF00]"
          : "border-gray-200 dark:border-[#1A1A1A]"
      }`}
    >
      {/* Pulse overlay */}
      {isPulsing && (
        <div className="absolute inset-0 bg-[#7C3AED]/10 dark:bg-[#CCFF00]/10 animate-pulse pointer-events-none" />
      )}

      {/* New tweet notification */}
      {isPulsing && newTweetsCount > 0 && (
        <div className="absolute top-2 right-2 z-10">
          <div className="flex items-center gap-1.5 bg-[#7C3AED] dark:bg-[#CCFF00] text-white dark:text-black px-3 py-1.5 rounded-[5px] text-xs font-bold animate-bounce">
            <Zap className="w-3 h-3" />
            +{newTweetsCount} NEW!
          </div>
        </div>
      )}

      {/* Header */}
      <div className="p-4 pb-2">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-[5px] ${isOnFire ? "bg-orange-500/20" : isCold ? "bg-cyan-500/20" : "bg-gray-100 dark:bg-white/10"}`}>
            {isOnFire ? (
              <Flame className="w-4 h-4 text-orange-500" />
            ) : isCold ? (
              <Snowflake className="w-4 h-4 text-cyan-400" />
            ) : (
              <Zap className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            )}
          </div>
          <div>
            <h3 className="text-gray-900 dark:text-white font-semibold text-sm">X Post Velocity</h3>
            <p className="text-gray-500 text-[10px]">Posts per hour</p>
          </div>
        </div>
      </div>

      {/* Gauge */}
      <div className="relative px-4 pb-2">
        <div className="relative h-24 flex items-center justify-center">
          {/* Semi-circle background */}
          <svg viewBox="0 0 200 110" className="w-full max-w-[200px]">
            {/* Background arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="rgba(0,0,0,0.1)"
              strokeWidth="12"
              strokeLinecap="round"
              className="dark:stroke-white/10"
            />

            {/* Cold zone (left) */}
            <path
              d="M 20 100 A 80 80 0 0 1 60 35"
              fill="none"
              stroke="rgba(0,212,255,0.3)"
              strokeWidth="12"
              strokeLinecap="round"
            />

            {/* Normal zone (center) */}
            <path
              d="M 60 35 A 80 80 0 0 1 140 35"
              fill="none"
              stroke="rgba(156,163,175,0.3)"
              strokeWidth="12"
              strokeLinecap="round"
              className="dark:stroke-white/30"
            />

            {/* Fire zone (right) */}
            <path
              d="M 140 35 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="rgba(255,107,53,0.3)"
              strokeWidth="12"
              strokeLinecap="round"
            />

            {/* Needle */}
            <g transform={`rotate(${gaugeAngle}, 100, 100)`}>
              <line
                x1="100"
                y1="100"
                x2="100"
                y2="35"
                stroke={gaugeColor}
                strokeWidth="3"
                strokeLinecap="round"
              />
              <circle
                cx="100"
                cy="100"
                r="8"
                fill={gaugeColor}
              />
              <circle
                cx="100"
                cy="100"
                r="4"
                fill="white"
                className="dark:fill-[#18181D]"
              />
            </g>

            {/* Labels */}
            <text x="15" y="108" fill="#00D4FF" fontSize="10" fontWeight="bold">SLOW</text>
            <text x="155" y="108" fill="#FF6B35" fontSize="10" fontWeight="bold">FIRE</text>
          </svg>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 pb-4">
        {/* Main velocity display */}
        <div className="text-center mb-3">
          <div className="flex items-center justify-center gap-2">
            <span
              className="text-3xl font-monument font-black"
              style={{ color: gaugeColor }}
            >
              {recentTweetsPerHour.toFixed(1)}
            </span>
            <span className="text-gray-500 text-sm">/hour</span>
            {trend === "up" && <TrendingUp className="w-5 h-5 text-green-500" />}
            {trend === "down" && <TrendingDown className="w-5 h-5 text-red-500" />}
          </div>
          <p className={`text-xs font-medium mt-1 ${
            isOnFire ? "text-orange-500" : isCold ? "text-cyan-400" : "text-gray-500 dark:text-gray-400"
          }`}>
            {isOnFire ? "🔥 ON FIRE! Posting fast!" :
             isCold ? "❄️ Quiet period..." :
             "⚡ Normal pace"}
          </p>
        </div>

        {/* Comparison stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-gray-100 dark:bg-white/5 rounded-[5px] px-3 py-2 text-center">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Today</p>
            <p className="text-gray-900 dark:text-white font-bold">{lastDayCount}</p>
          </div>
          <div className="bg-gray-100 dark:bg-white/5 rounded-[5px] px-3 py-2 text-center">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Avg/day</p>
            <p className="text-gray-900 dark:text-white font-bold">{(totalTweets / totalDays).toFixed(0)}</p>
          </div>
        </div>

        {/* Velocity comparison bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
            <span>vs Average</span>
            <span className={velocityRatio >= 1 ? "text-green-500" : "text-red-500"}>
              {velocityRatio >= 1 ? "+" : ""}{((velocityRatio - 1) * 100).toFixed(0)}%
            </span>
          </div>
          <div className="h-1.5 bg-gray-200 dark:bg-white/10 rounded-[5px] overflow-hidden">
            <div
              className="h-full rounded-[5px] transition-all duration-500"
              style={{
                width: `${Math.min(velocityRatio * 50, 100)}%`,
                backgroundColor: gaugeColor
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
